import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, KeyRound, Shield, AlertCircle } from 'lucide-react';

interface ChangePasswordModalProps {
  onClose: () => void;
  onSubmit: (newPassword: string) => void;
  email: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose, onSubmit, email }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    const passwordRegex = {
      minLength: /.{8,}/,
      upper: /[A-Z]/,
      lower: /[a-z]/,
      digit: /[0-9]/,
      special: /[!@#$%^&*]/,
    };

    if (!passwordRegex.minLength.test(newPassword)) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!passwordRegex.upper.test(newPassword)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!passwordRegex.lower.test(newPassword)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    if (!passwordRegex.digit.test(newPassword)) {
      setError('Password must contain at least one number.');
      return;
    }
    if (!passwordRegex.special.test(newPassword)) {
      setError('Password must include a special character (!@#$%^&*).');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    onSubmit(newPassword);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 font-sans">
      <div className="bg-neutral-900 text-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-neutral-800 transition-all duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-900/20 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-bold">Update Password</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-neutral-800/30 p-3 rounded-2xl border border-neutral-800">
            <p className="text-[10px] uppercase font-black tracking-widest text-gray-400 mb-1">Target Account</p>
            <p className="text-sm font-bold text-gray-300 truncate">{email}</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">New Secure Password</label>
              <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
                <Lock className="w-4 h-4 text-gray-400" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-400"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Confirm New Password</label>
              <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
                <Shield className="w-4 h-4 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-400"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-800/50 p-3 rounded-xl">
              <p className="text-red-400 text-xs font-bold flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 bg-black/10 border-t border-neutral-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-gray-400 text-sm font-bold hover:bg-neutral-800 transition-all"
          >
            Go Back
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-lg shadow-amber-600/20 active:scale-95 transition-all"
          >
            Update Password
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
